import React, { useState } from "react";
import { T } from "../theme/tokens.js";
import { Modal, Btn, Field, Labelled, Notice } from "./ui/index.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { friendlyError } from "../lib/supabase.js";

/* Changing your own password without leaving the app.
 *
 * The alternative is the forgotten password email, which depends on the mail
 * service working, on the link surviving a corporate scanner that opens mail
 * before you do, and on nobody having hit the sending limit that hour. None
 * of that is in our hands. This is, and it works while signed in.
 */

const MIN = 8;

export default function ChangePassword({ onClose }) {
  const { updatePassword, user } = useAuth();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN;
  const mismatch = again.length > 0 && again !== password;
  const ready = password.length >= MIN && again === password;

  const save = async () => {
    if (!ready) return;
    setError("");
    setBusy(true);
    const { error: failed } = await updatePassword(password);
    setBusy(false);
    if (failed) setError(friendlyError(failed));
    else setDone(true);
  };

  return (
    <Modal title="Change your password" onClose={onClose}>
      {done ? (
        <div style={{ display: "grid", gap: 14 }}>
          <Notice kind="ok">
            Saved. Use the new one next time you sign in. You stay signed in here.
          </Notice>
          <Btn full onClick={onClose}>Close</Btn>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.6 }}>
            Changing the password for {user?.email}. No email is sent and no
            link is involved, so nothing can eat it on the way.
          </div>

          <Labelled label="New password" hint={`At least ${MIN} characters`}>
            <Field f={{ type: "password" }} value={password} onChange={setPassword}
              autoFocus placeholder={`At least ${MIN} characters`} />
          </Labelled>

          <Labelled label="Type it again">
            <Field f={{ type: "password" }} value={again} onChange={setAgain}
              placeholder="The same again" />
          </Labelled>

          {tooShort && <Notice>Passwords need at least {MIN} characters.</Notice>}
          {mismatch && <Notice>Those two do not match yet.</Notice>}
          {error && <Notice kind="error">{error}</Notice>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={busy || !ready}>
              {busy ? "Saving..." : "Save password"}
            </Btn>
          </div>

          <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.6 }}>
            Save it somewhere before you close this. Nobody, Heidi included, can
            read a password back to you.
          </div>
        </div>
      )}
    </Modal>
  );
}
